import { put, takeLatest, call, cancel, delay, fork, take, select, all } from 'redux-saga/effects';
import {
	fetchOrdersSuccess,
	fetchOrdersError,
	fetchLastOrderSuccess,
	fetchLastOrderError,
	fetchOrderInvoiceSuccess,
	fetchOrderInvoiceError,
	setPendingOrders,
	selectOrder,
	cancelPendingOrderSuccess,
	setAllLiveOrders,
} from '../actions/orders';
import {
	FETCH_ORDERS,
	FETCH_LAST_ORDER,
	FETCH_ORDER_INVOICE,
	SYNC_PENDING_ORDERS,
	CANCEL_SYNC_PENDING_ORDERS,
	CANCEL_PENDING_ORDER,
	POOL_ACTIVE_ORDER,
	CANCEL_POOL_ACTIVE_ORDER,
	CANCEL_VS_ORDER,
	POOL_ALL_LIVE_ORDERS,
	CANCEL_POOL_ALL_LIVE_ORDERS,
} from '../actions/types';
import {
	fetchOrders as fetchOrdersAPI,
	fetchLastOrder as fetchLastOrderAPI,
	fetchOrderInvoice as fetchOrderInvoiceAPI,
	fetchPendingOrders as fetchPendingOrdersAPI,
	cancelOrder as cancelOrderAPI,
	cancelVSOrder as cancelVSOrderAPI,
} from '../services/orders';
import { showErrorMsg, showSuccessMsg } from '../actions/snackbar';
import { updateCartSuccess, clearLastTransaction, toggleResetCartDlg, throttleSaveCart } from '../actions/cart';
import { updateSkuPurchareCount } from '../actions/skus';
import { validateSession } from './session';
import { VSHELF_MACHINE_SKU } from '../constants';
import { updateVSkuPurchareCount } from '../actions/virtual-shelf';
import { CANCELLED_ORDER, COMPLETED_ORDER, FAILED_ORDER, PARTIAL_ORDER } from '../constants';

const DONE_ORDER_STATUS_LIST = [CANCELLED_ORDER, COMPLETED_ORDER, FAILED_ORDER, PARTIAL_ORDER];

function* fetchOrders(action) {
	try {
		const { sessionId } = yield select((state) => state.auth || {});
		const { orderHistory = [] } = yield select((state) => state.orders || {});
		const { mobile, page = 0 } = action.payload;
		let orders = yield call(validateSession, fetchOrdersAPI, mobile, page, sessionId);
		orders = orders.map((order) => {
			let { order_items } = order;
			if (order_items) {
				order_items = Object.values(order_items);
			}
			return { ...order, order_items };
		});

		if (page > 0) {
			orders = [...orderHistory, ...orders];
		}

		yield put(fetchOrdersSuccess(orders));
	} catch (error) {
		yield put(fetchOrdersError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* fetchLastOrder(action) {
	try {
		const { sessionId } = yield select((state) => state.auth || {});
		const lastOrder = yield call(fetchLastOrderAPI, action.payload, sessionId);
		// const lastOrder = yield new Promise((res) => setTimeout(() => res(), 10000));
		yield put(fetchLastOrderSuccess(lastOrder));
	} catch (error) {
		yield put(fetchLastOrderError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* fetchOrderInvoice(action) {
	try {
		const { sessionId } = yield select((state) => state.auth || {});
		const orderInvoice = yield call(validateSession, fetchOrderInvoiceAPI, action.payload, sessionId);
		let { order_items } = orderInvoice;
		if (order_items) {
			order_items = Object.values(order_items);
		}
		yield put(fetchOrderInvoiceSuccess({ ...orderInvoice, order_items }));
	} catch (error) {
		yield put(fetchOrderInvoiceError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* syncPendingOrders(action) {
	const { payload: { mobile, delay: delayTime = 1000 * 60 } = {} } = action;
	try {
		while (true) {
			const { sessionId } = yield select((state) => state.auth || {});
			let pendingOrders = yield call(validateSession, fetchPendingOrdersAPI, mobile, sessionId);
			if (pendingOrders.length > 0) {
				yield put(setPendingOrders(pendingOrders));
				// const { selectedOrder } = yield select((state) => state.orders);
				// if (selectedOrder) {
				// 	const newSelOrder = pendingOrders.find((order) => order.order_id === selectedOrder.order_id);
				// 	if (newSelOrder) {
				// 		yield put(selectOrder(newSelOrder));
				// 	}
				// }
				yield delay(delayTime);
			} else {
				yield put(setPendingOrders([]));
				yield cancel();
			}
		}
	} catch (error) {
		yield put(showErrorMsg(error.message));
		yield put(setPendingOrders([]));
	}
}

function* startSyncPendingOrders(action) {
	const syncPending = yield fork(syncPendingOrders, action);
	yield take(CANCEL_SYNC_PENDING_ORDERS);
	// user clicked stop. cancel the background task
	// this will cause the forked syncPendingOrders task to jump into its finally block
	yield cancel(syncPending);
}

function* cancelPendingOrder(action) {
	const { orderId, updateCart } = action.payload || {};
	try {
		const { sessionId } = yield select((state) => state.auth || {});
		const { mobile } = yield select((state) => state.user || {});
		const res = yield call(validateSession, cancelOrderAPI, orderId, sessionId);
		const { selectedOrder } = yield select((state) => state.orders || {});
		if (updateCart) {
			const { lastTransaction } = yield select((state) => state.cart || {});
			if (lastTransaction) {
				yield put(updateCartSuccess(lastTransaction));
				yield put(clearLastTransaction());
				const { skuData: { isVirtualSku = false, action } = {} } = lastTransaction;
				if (isVirtualSku) {
					yield put(updateVSkuPurchareCount(lastTransaction));
					if (action === VSHELF_MACHINE_SKU) {
						yield put(updateSkuPurchareCount(lastTransaction));
					}
				} else {
					yield put(updateSkuPurchareCount(lastTransaction));
					if (action === VSHELF_MACHINE_SKU) {
						yield put(updateVSkuPurchareCount(lastTransaction));
					}
				}
				yield put(toggleResetCartDlg(false));
				yield put(throttleSaveCart(mobile, sessionId, 'PRODUCTS'));
			}
		}

		if (selectedOrder && selectedOrder.order_id === orderId) {
			const { orderStatus, msg } = res;

			if (orderStatus !== 'Cancelled') {
				yield put(showErrorMsg(msg));
			}

			yield put(
				selectOrder({
					...selectedOrder,
					status: orderStatus,
				})
			);
		}

		yield put(setPendingOrders([]));
		yield put(cancelPendingOrderSuccess(orderId));
		yield put(
			showSuccessMsg(
				`Order cancelled successfully.
      Paid amount will reflect in your wallet
    `,
				5000
			)
		);
	} catch (error) {
		yield put(showErrorMsg(error.message));
		yield put(cancelPendingOrderSuccess(orderId));
	}
}

function* poolActiveOrder(action) {
	const delayTime = 1000 * 5;
	try {
		while (true) {
			const { sessionId } = yield select((state) => state.auth || {});
			const orderInvoice = yield call(validateSession, fetchOrderInvoiceAPI, action.payload, sessionId);
			const { selectedOrder = {} } = yield select((state) => state.orders || {});
			const { status: oldStatus, order_modified_at: oldLastModified = new Date() } = selectedOrder || {};
			const oldDate = new Date(oldLastModified);
			const { status, order_modified_at } = orderInvoice;
			const date = new Date(order_modified_at);
			if (!DONE_ORDER_STATUS_LIST.includes(oldStatus) && date.getTime() > oldDate.getTime()) {
				let { order_items } = orderInvoice;
				if (order_items) {
					order_items = Object.values(order_items);
				}
				yield put(fetchOrderInvoiceSuccess({ ...orderInvoice, order_items }));
			}

			if (!DONE_ORDER_STATUS_LIST.includes(status) && !DONE_ORDER_STATUS_LIST.includes(oldStatus)) {
				yield delay(delayTime);
			} else {
				yield cancel();
			}
		}
	} catch (error) {
		yield put(showErrorMsg(error.message));
	}
}

function* startPoolActiveOrder(action) {
	const poolingActiveOrder = yield fork(poolActiveOrder, action);
	yield take(CANCEL_POOL_ACTIVE_ORDER);
	// this will cause the forked poolActiveOrder task to jump into its finally block
	yield cancel(poolingActiveOrder);
}

function* cancelVSOrder(action) {
	const { orderId } = action.payload || {};
	try {
		const { sessionId } = yield select((state) => state.auth || {});
		const { selectedOrder } = yield select((state) => state.orders || {});
		yield call(validateSession, cancelVSOrderAPI, orderId, sessionId);

		if (selectedOrder && selectedOrder.order_id === orderId) {
			yield put(
				selectOrder({
					...selectedOrder,
					status: CANCELLED_ORDER,
				})
			);
		}

		yield put(cancelPendingOrderSuccess(orderId));
		yield put(
			showSuccessMsg(
				`Order cancelled successfully.
      Paid amount will reflect in your wallet
    `,
				5000
			)
		);
	} catch (error) {
		yield put(showErrorMsg(error.message));
		yield put(cancelPendingOrderSuccess(orderId));
	}
}

function* startPoolLiveOrders(action) {
	const poolingLiveOrders = yield fork(poolLiveOrders, action);
	yield take(CANCEL_POOL_ALL_LIVE_ORDERS);
	// this will cause the forked poolActiveOrder task to jump into its finally block
	yield cancel(poolingLiveOrders);
}

function* poolLiveOrders(action) {
	const delayTime = 1000 * 10;

	try {
		const { sessionId } = yield select((state) => state.auth || {});
		const { payload: orders = [] } = action;
		// let liveOrders = orders;
		let liveOrders = orders.reduce((orders, order, index) => {
			const { status } = order;
			if (!DONE_ORDER_STATUS_LIST.includes(status)) {
				orders.push({ ...order, index });
			}
			return orders;
		}, []);

		let orderData = [...orders];
		while (liveOrders.length > 0) {
			const genIterators = liveOrders.map(({ order_id: orderId, index }) =>
				call(validateSession, fetchOrderInvoiceAPI, { orderId }, sessionId)
			);
			const res = yield all(genIterators);
			const newLiveOrders = [];
			let bOrdersUpdated = false;

			// eslint-disable-next-line no-loop-func
			liveOrders.forEach((order = {}, index) => {
				const { status: oldStatus, index: orderIndex } = order;
				const orderInvoice = res[index];
				const { status } = orderInvoice;
				if (status !== oldStatus) {
					let { order_items } = orderInvoice;
					if (order_items) {
						orderInvoice.order_items = Object.values(order_items);
					}

					orderData[orderIndex] = { ...orderInvoice };
					bOrdersUpdated = true;
				}

				if (!DONE_ORDER_STATUS_LIST.includes(status)) {
					newLiveOrders.push({ ...orderInvoice, index: orderIndex });
				}
			});

			orderData = [...orderData];
			liveOrders = newLiveOrders;
			if (bOrdersUpdated) {
				yield put(setAllLiveOrders(orderData));
			}

			if (liveOrders.length > 0) {
				yield delay(delayTime);
			} else {
				yield cancel();
			}
		}
	} catch (error) {
		yield put(showErrorMsg(error.message));
	}
}

export function* watchOrders() {
	yield takeLatest(FETCH_ORDERS, fetchOrders);
	yield takeLatest(FETCH_LAST_ORDER, fetchLastOrder);
	yield takeLatest(FETCH_ORDER_INVOICE, fetchOrderInvoice);
	yield takeLatest(SYNC_PENDING_ORDERS, startSyncPendingOrders);
	yield takeLatest(CANCEL_PENDING_ORDER, cancelPendingOrder);
	yield takeLatest(POOL_ACTIVE_ORDER, startPoolActiveOrder);
	yield takeLatest(CANCEL_VS_ORDER, cancelVSOrder);
	yield takeLatest(POOL_ALL_LIVE_ORDERS, startPoolLiveOrders);
}
